import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, Link } from "react-router-dom";
import { Button, Card, CardBody, Col, Collapse, Container, Row, Table } from "reactstrap";
import Breadcrumb from "../../../layout/breadcrumb";
import PreLoader from "../../../share-components/ui/preLoader";
import withReducer from "../../../store/withReducer";
import { formatterCurrency } from "../../../utils/common";
import orderReducer from "../store";
import {
    clearFrasersPaymentDetails,
    getFrasersPaymentById,
    refreshFrasersPayment,
} from "../store/frasersPlusSlice";
import { NOT_PAID_STATUSES, creditBadge, creditRowBadge, paymentBadge } from "./constants";
import "./styles.scss";

/**
 * One Frasers Plus payment, end to end.
 *
 * This is the screen someone opens when a customer says "I returned it and I'm
 * still being charged" — so it leads with what happened at each step and where
 * it stopped, then gives the buttons to put it right.
 *
 * The page is deliberately split in two. An order has exactly ONE payment but
 * can have MANY refunds, so mixing them into a single timeline falls apart the
 * moment a customer returns three things on three different days. "Getting
 * paid" is always the same five steps; each refund then gets its own small
 * journey underneath.
 *
 * Everything shown comes from the events the backend recorded as things
 * happened, so what is on screen is what actually occurred, retries included.
 */

/** Horizontal step boxes, coloured by state. Used for the payment and for each refund. */
const Timeline = ({ events, idPrefix }) => (
    <div className="frasers-payment-timeline">
        {events.map((event, index) => (
            <div
                key={`${idPrefix}-step-${index}`}
                className={`frasers-step ${event.state === "error" ? "is-error" : ""} ${
                    event.state === "pending" ? "is-pending" : ""
                }`}
            >
                <p className="p-0 m-0 text-dark frasers-step-name">{event.name}</p>
                <p className="p-0 m-0 text-muted frasers-step-time">{event.at}</p>
            </div>
        ))}
    </div>
);

/** The same steps in words, because the boxes only carry the headline. */
const StepTable = ({ events }) => (
    <Table className="mt-2" borderless>
        <tbody>
            {events.map((event, index) => (
                <tr key={`row-${index}`}>
                    <td style={{ width: "200px", paddingLeft: 0 }}>
                        <strong>{event.name}</strong>
                        <br />
                        <span className="text-muted frasers-step-time">{event.at}</span>
                    </td>
                    <td className={event.state === "error" ? "font-danger" : ""}>{event.detail}</td>
                </tr>
            ))}
        </tbody>
    </Table>
);

/** One collapsible request/response pair in the technical panel. */
const ApiCall = ({ call }) => {
    const [open, setOpen] = useState(false);

    return (
        <div className={`frasers-api-call ${call.ok ? "" : "is-error"}`}>
            <div style={{ cursor: "pointer" }} onClick={() => setOpen(!open)}>
                <i className={`fa fa-angle-${open ? "down" : "right"} mr-2`}></i>
                <strong>{call.label}</strong>
                <span className="text-muted">
                    {" "}
                    · {call.method} {call.path}
                </span>
                {call.httpStatus ? (
                    <span className={`badge badge-${call.ok ? "success" : "danger"} ml-2`}>{call.httpStatus}</span>
                ) : null}
                <span className="text-muted frasers-step-time float-right">
                    {call.at} · {call.source}
                </span>
            </div>
            <Collapse isOpen={open}>
                <div className="mt-2">
                    {call.request ? (
                        <>
                            <p className="mb-1 frasers-step-time text-muted">Request</p>
                            <div className="frasers-raw">{JSON.stringify(call.request, null, 2)}</div>
                        </>
                    ) : null}
                    <p className="mb-1 mt-2 frasers-step-time text-muted">Response</p>
                    <div className="frasers-raw">{JSON.stringify(call.response, null, 2)}</div>
                </div>
            </Collapse>
        </div>
    );
};

const ViewFrasersPlusPayment = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const payment = useSelector(({ order }) => order.frasersPlus.details);

    const dataMenu = [
        {
            type: 1,
            text: "Frasers Plus Payments",
            link: "/order/frasers-plus-payments",
        },
        {
            type: 0,
            text: payment ? payment.orderName : "Payment",
        },
    ];

    useEffect(() => {
        // WHY the guard: the router keeps a page mounted for its exit animation
        // after its route stops matching, and during that moment useParams()
        // returns {} - so `id` flips to undefined and this effect runs again.
        // Without the guard that fires GET .../undefined, the API answers 404,
        // and the global interceptor bounces the user to /error/not-found just
        // as the list page has finished loading.
        if (!id) {
            return undefined;
        }

        setLoading(true);
        dispatch(getFrasersPaymentById(id))
            .then((res) => {
                setLoading(false);
                if (res.error) {
                    history.push("/order/frasers-plus-payments");
                }
            })
            .catch(() => {
                setLoading(false);
                history.push("/order/frasers-plus-payments");
            });

        return () => {
            dispatch(clearFrasersPaymentDetails());
        };
    }, [dispatch, id, history]);

    if (loading && !payment) {
        return <PreLoader />;
    }

    if (!payment) {
        return (
            <Fragment>
                <Breadcrumb title="Frasers Plus Payment" data={dataMenu} />
                <Container fluid={true}>
                    <Card>
                        <CardBody>
                            <p>Payment not found.</p>
                            <Button color="primary" onClick={() => history.push("/order/frasers-plus-payments")}>
                                Back to Frasers Plus Payments
                            </Button>
                        </CardBody>
                    </Card>
                </Container>
            </Fragment>
        );
    }

    const outstanding = Math.max(0, Number((payment.amount - payment.credited).toFixed(2)));
    const refusedCredits = payment.credits.filter((c) => c.refused);
    const unrecordedCredits = payment.credits.filter((c) => c.needsShopifyRefund);
    const notPaid = NOT_PAID_STATUSES.indexOf(payment.status) !== -1;

    const orderLink = payment.orderId ? `${process.env.PUBLIC_URL}/order/${payment.orderId}/view` : null;

    const refresh = () => {
        setBusy(true);
        dispatch(refreshFrasersPayment(payment.id)).finally(() => setBusy(false));
    };

    return (
        <Fragment>
            <Breadcrumb title={`Frasers Plus — ${payment.orderName}`} data={dataMenu} />
            <Container fluid={true} className="frasers-payment-detail">
                {/* ---------------------------------------------------------- */}
                {/* What is wrong, in one line, before anything else.           */}
                {/* Credits are sent to Frasers BEFORE the Shopify refund, so   */}
                {/* there are exactly two ways it can be half done.             */}
                {/* ---------------------------------------------------------- */}
                {unrecordedCredits.length > 0 && (
                    <Row>
                        <Col md="12">
                            <Card className="mb-3 b-l-danger border-3">
                                <CardBody className="p-3">
                                    <h6 className="mb-1 font-danger">Credited at Frasers, but not refunded on Shopify</h6>
                                    <p className="mb-2">
                                        Frasers accepted{" "}
                                        {unrecordedCredits.map((c) => formatterCurrency(c.amount, payment.currency)).join(" and ")}{" "}
                                        for this customer, but Shopify rejected the refund afterwards, so the order still shows nothing
                                        refunded and stock has not been restocked. Re-issue the refund from the order for exactly that
                                        amount — it will be matched to the existing credit, not sent to Frasers again.
                                    </p>
                                    {orderLink ? (
                                        <Link className="btn btn-danger btn-sm" to={orderLink} target="_blank">
                                            Open the order
                                        </Link>
                                    ) : null}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}

                {refusedCredits.length > 0 && unrecordedCredits.length === 0 && (
                    <Row>
                        <Col md="12">
                            <Card className="mb-3 b-l-warning border-3">
                                <CardBody className="p-3">
                                    <h6 className="mb-1 font-warning">Frasers refused a credit — no refund was made</h6>
                                    <p className="mb-2">
                                        {refusedCredits[refusedCredits.length - 1].failureReason || "Frasers Plus did not accept the credit"}.
                                        Because Frasers are asked first, nothing was refunded on Shopify and the customer has not received
                                        anything. Once the cause is resolved, re-issue the refund from the order.
                                    </p>
                                    {orderLink ? (
                                        <Link className="btn btn-warning btn-sm" to={orderLink} target="_blank">
                                            Open the order
                                        </Link>
                                    ) : null}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}

                {notPaid && (
                    <Row>
                        <Col md="12">
                            <Card className="mb-3 b-l-warning border-3">
                                <CardBody className="p-3">
                                    <h6 className="mb-1 font-warning">This order has not been paid</h6>
                                    <p className="mb-0">
                                        {payment.lastError || `${payment.paymentStatus}.`} The order is still open — the customer can
                                        start a new Frasers Plus attempt from their account page, or customer services can help.
                                    </p>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* ---------------------------------------------------------- */}
                {/* Summary                                                     */}
                {/* ---------------------------------------------------------- */}
                <Row>
                    <Col md="12">
                        <Card className="ribbon-wrapper mb-3">
                            <CardBody>
                                <div className="ribbon ribbon-clip h6 ribbon-info">{payment.orderName}</div>

                                <div className="table-responsive">
                                    <Table style={{ width: "100%", border: "none" }}>
                                        <tbody>
                                            <tr>
                                                <td style={{ width: "50%", paddingLeft: 0 }}>
                                                    <div className="frasers-fact">
                                                        <strong>Payment status:</strong>{" "}
                                                        <span className={`badge badge-${paymentBadge(payment.status)}`}>
                                                            {payment.paymentStatus}
                                                        </span>
                                                    </div>
                                                    <div className="frasers-fact">
                                                        <strong>Credit status:</strong>{" "}
                                                        <span className={`badge badge-${creditBadge(payment.creditStatusKey)}`}>
                                                            {payment.creditStatus}
                                                        </span>
                                                    </div>
                                                    <div className="frasers-fact">
                                                        <strong>Payment date:</strong> {payment.orderDate}
                                                    </div>
                                                    <div className="frasers-fact">
                                                        <strong>Settled via:</strong> {payment.settledVia || "-"}
                                                    </div>
                                                    <div className="frasers-fact">
                                                        <strong>Shopify order:</strong>{" "}
                                                        {payment.orderId ? (
                                                            <Link
                                                                className="text-primary"
                                                                target="_blank"
                                                                to={`${process.env.PUBLIC_URL}/order/${payment.orderId}/view`}
                                                            >
                                                                {payment.orderName}
                                                            </Link>
                                                        ) : (
                                                            payment.orderName
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="frasers-fact">
                                                        <strong>Amount on credit:</strong>{" "}
                                                        {formatterCurrency(payment.amount, payment.currency)}
                                                    </div>
                                                    <div className="frasers-fact">
                                                        <strong>Credited back:</strong>{" "}
                                                        {payment.credited > 0 ? formatterCurrency(payment.credited, payment.currency) : "-"}
                                                    </div>
                                                    <div className="frasers-fact">
                                                        <strong>Still owed to Frasers:</strong>{" "}
                                                        {formatterCurrency(outstanding, payment.currency)}
                                                    </div>
                                                    <div className="frasers-fact">
                                                        <strong>Refunds on this order:</strong> {payment.credits.length}
                                                        {refusedCredits.length > 0 ? (
                                                            <span className="font-danger"> ({refusedCredits.length} refused by Frasers)</span>
                                                        ) : null}
                                                        {unrecordedCredits.length > 0 ? (
                                                            <span className="font-danger"> ({unrecordedCredits.length} not recorded on Shopify)</span>
                                                        ) : null}
                                                    </div>
                                                    <div className="frasers-fact">
                                                        <strong>Customer:</strong> {payment.customer || "-"}
                                                    </div>
                                                    <div className="frasers-fact">
                                                        <strong>Phone sent to Frasers:</strong>{" "}
                                                        {payment.phone || (
                                                            <span className="font-danger">none — customer forced through full onboarding</span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </div>

                                <div className="mt-2">
                                    <Button color="primary" size="sm" className="mr-2" disabled={busy} onClick={refresh}>
                                        Refresh from Frasers
                                    </Button>{" "}
                                    <Button color="secondary" size="sm" onClick={() => history.push("/order/frasers-plus-payments")}>
                                        Back to list
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* ---------------------------------------------------------- */}
                {/* The payment itself — always these five steps, however many  */}
                {/* refunds land later                                          */}
                {/* ---------------------------------------------------------- */}
                <Row>
                    <Col md="12">
                        <Card className="mb-3">
                            <CardBody>
                                <h6 className="mb-3">Getting paid</h6>
                                <Timeline events={payment.events} idPrefix="pay" />
                                <StepTable events={payment.events} />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* ---------------------------------------------------------- */}
                {/* Refunds. One block per Shopify refund, each with its own    */}
                {/* journey — this is what keeps the screen readable when an    */}
                {/* order is refunded three or four times.                      */}
                {/* ---------------------------------------------------------- */}
                <Row>
                    <Col md="12">
                        <Card className="mb-3">
                            <CardBody>
                                <h6 className="mb-3">
                                    Giving money back
                                    {payment.credits.length > 0 ? (
                                        <span className="text-muted">
                                            {" "}
                                            — {payment.credits.length} refund{payment.credits.length === 1 ? "" : "s"},{" "}
                                            {formatterCurrency(payment.credited, payment.currency)} credited of{" "}
                                            {formatterCurrency(payment.amount, payment.currency)}
                                        </span>
                                    ) : null}
                                </h6>

                                {payment.credits.length === 0 ? (
                                    <p className="text-muted mb-0">
                                        Nothing has been refunded on this order, so nothing has been credited back to Frasers.
                                    </p>
                                ) : (
                                    payment.credits.map((credit, index) => (
                                        <div
                                            key={credit.id}
                                            className={`frasers-refund-block ${credit.refused || credit.needsShopifyRefund ? "is-error" : ""}`}
                                        >
                                            <div className="frasers-refund-head">
                                                <Row className="align-items-center">
                                                    <Col md="8">
                                                        <span className="frasers-refund-index">Refund {index + 1}</span>
                                                        <strong className="ml-2">{formatterCurrency(credit.amount, payment.currency)}</strong>
                                                        <span className="text-muted"> · {credit.reason}</span>
                                                        <div className="text-muted frasers-step-time">
                                                            {credit.date} ·{" "}
                                                            {credit.shopifyRefundId ? (
                                                                `Shopify refund ${credit.shopifyRefundId}`
                                                            ) : (
                                                                <span className="font-danger">not recorded on Shopify</span>
                                                            )}
                                                            {credit.attempts > 1 ? ` · ${credit.attempts} attempts` : ""}
                                                        </div>
                                                    </Col>
                                                    <Col md="4" className="text-right">
                                                        <span className={`badge badge-${creditRowBadge(credit.statusKey)}`}>
                                                            {credit.refused ? "Refused" : credit.status}
                                                        </span>
                                                    </Col>
                                                </Row>
                                            </div>

                                            <Timeline events={credit.events} idPrefix={`credit-${credit.id}`} />

                                            <StepTable events={credit.events} />

                                            {credit.failureReason ? (
                                                <p className="font-danger mb-0 frasers-step-time">{credit.failureReason}</p>
                                            ) : null}
                                        </div>
                                    ))
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* ---------------------------------------------------------- */}
                {/* For a developer chasing a specific failure. One entry per   */}
                {/* call to Frasers, so a refund's own request and response are */}
                {/* never buried under another refund's.                        */}
                {/* ---------------------------------------------------------- */}
                <Row>
                    <Col md="12">
                        <Card className="mb-3">
                            <CardBody>
                                <h6 className="mb-3">
                                    Technical details
                                    <span className="text-muted"> — {payment.apiCalls.length} calls</span>
                                </h6>

                                <div className="frasers-fact">
                                    <strong>Frasers reference (UPR):</strong> {payment.upr || "none — authorisation never succeeded"}
                                </div>
                                <div className="frasers-fact">
                                    <strong>Our payment id:</strong> {payment.paymentId}
                                </div>
                                <div className="frasers-fact mb-2">
                                    <strong>Customer email:</strong> {payment.email || "-"}
                                </div>

                                {payment.apiCalls.map((call) => (
                                    <ApiCall key={`api-${call.id}`} call={call} />
                                ))}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default withReducer([{ order: orderReducer }])(ViewFrasersPlusPayment);
